// God → Gateway → Payins / Payouts: every gateway transaction across all merchants,
// live, with the full record, bank callbacks, merchant callbacks and ledger rows.
// Payouts held in "review" are settled here after checking with ProtonPay.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownToLine, BookOpen, Clock, CreditCard, FileText, Radio, RefreshCw, Send, User, Webhook } from "lucide-react";
import { god } from "../../godApi";
import { useDebounced, usePagedList, usePolling, useSyncFilter } from "../../../components/sc/ui";
import {
  Button, Callout, Cell, Chips, Code, Dialog, Empty, Field, Input, Page, Pagination, Panel, PanelHero, Pill, Rows,
  SearchInput, Section, Select, Skeleton, StatusPill, Table, Timeline, Toggle, Toolbar, ago, inr, shortId, when,
} from "../../kit";

const rup = (p) => (p == null ? null : p / 100);
const STATUSES = {
  payins: ["initiated", "pending", "success", "failed", "dropped", "dispute", "error"],
  payouts: ["pending", "review", "success", "failed", "error"],
};
const LABEL = { initiated: "Initiated", pending: "Pending", review: "In review", success: "Success", failed: "Failed", dropped: "Dropped", dispute: "Dispute", error: "Error" };

export default function GwTxns({ kind }) {
  const payin = kind === "payins";
  const [qInput, setQInput] = useState("");
  const q = useDebounced(qInput);
  const [live, setLive] = useState(true);
  const [open, setOpen] = useState(null);
  const [counts, setCounts] = useState(null);
  const list = usePagedList((p) => god.gwList(kind, p), { status: "all", q: "", date_from: "", date_to: "" }, 25);
  useSyncFilter(list.setFilter, "q", q);
  usePolling(list.load, 5000, live && list.page === 1);
  const [job, setJob] = useState(null);
  const rechecking = job?.state === "running";
  useEffect(() => {
    if (!rechecking) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await god.gwRecheckProgress();
        setJob(r);
        list.load();
        if (r.state !== "running") toast.success(r.total ? `Re-checked ${r.total}: ${r.paid} paid, ${r.closed} closed, ${r.still_open} still waiting${r.errors ? `, ${r.errors} errors` : ""}` : "No pending payins");
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [rechecking, list.load]);
  const recheck = async () => {
    try { setJob(await god.gwRecheckPending()); } catch (e) { toast.error(e.message); }
  };

  // Status counts (all time, all merchants) come from the gateway overview.
  const loadCounts = useCallback(() => god.gwOverview().then((o) => setCounts(o[kind] || {})).catch(() => {}), [kind]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  usePolling(loadCounts, 30000, live);

  const total = counts ? Object.values(counts).reduce((a, s) => a + (s.count || 0), 0) : null;
  const chips = [{ key: "all", label: "All", count: total }]
    .concat(STATUSES[kind].map((s) => ({ key: s, label: LABEL[s], count: counts ? counts[s]?.count || 0 : null })));

  const columns = [
    { key: "merchant", label: "Merchant", render: (t) => <Cell main={t.merchant_name || "—"} sub={t.source} /> },
    { key: "order", label: "Order", render: (t) => <Cell mono main={t.order_ref} sub={shortId(payin ? t.payin_id : t.payout_id)} /> },
    { key: "amount", label: "Amount", align: "right", render: (t) => <b>{inr(rup(t.amount_paisa))}</b> },
    payin
      ? { key: "utr", label: "UTR / VPA", hideSm: true, render: (t) => <Cell mono main={t.payment_id} sub={t.payer_vpa} /> }
      : { key: "to", label: "Beneficiary", hideSm: true, render: (t) => <Cell main={t.account_holder_name} sub={`••${(t.account_number || "").slice(-4)} · ${t.ifsc_code || ""}`} /> },
    { key: "at", label: "Created", hideSm: true, render: (t) => <Cell main={when(t.created_at)} sub={`updated ${ago(t.updated_at)}`} /> },
    { key: "status", label: "Status", render: (t) => <StatusPill status={t.status} /> },
  ];

  return (
    <Page>
      <Section title={payin ? "All payins" : "All payouts"} icon={payin ? ArrowDownToLine : Send} pad={false}
        sub={`${list.data.total.toLocaleString("en-IN")} matching`}
        actions={<>
          {payin && <Button size="sm" icon={RefreshCw} disabled={rechecking} onClick={recheck} data-testid="god-gw-recheck">{rechecking ? `Checking ${job.done}/${job.total} · ${job.paid} paid` : "Re-check pending"}</Button>}
          <span className="gwt-live"><Toggle on={live} onChange={setLive} label="Live updates" />{live && list.page === 1 ? "Live" : "Paused"}</span>
        </>}>
        <Toolbar>
          <SearchInput value={qInput} onChange={setQInput} testId={`god-gw-${kind}-search`}
            placeholder={payin ? "Name, phone, order, UTR, VPA, email, ProtonPay id…" : "Name, account, order, UTR, IFSC, ProtonPay id…"} />
          <Input type="date" aria-label="From date" title="From" value={list.filters.date_from} onChange={(e) => list.setFilter("date_from", e.target.value)} />
          <Input type="date" aria-label="To date" title="To" value={list.filters.date_to} onChange={(e) => list.setFilter("date_to", e.target.value)} />
          {(list.filters.date_from || list.filters.date_to) && (
            <Button variant="ghost" size="sm" onClick={() => { list.setFilter("date_from", ""); list.setFilter("date_to", ""); }}>Clear dates</Button>
          )}
        </Toolbar>
        <Toolbar>
          <Chips options={chips} value={list.filters.status} onChange={(v) => list.setFilter("status", v)} />
        </Toolbar>
        {list.error && <div className="gwt-pad"><Callout tone="red" title="Couldn't load">{list.error}</Callout></div>}
        <Table testId={`god-gw-${kind}`} columns={columns} rows={list.data.items} loading={list.loading}
          onRowClick={(t) => setOpen(t.id)} activeId={open}
          empty={<Empty icon={payin ? ArrowDownToLine : Send} title={payin ? "No payins found" : "No payouts found"}>
            {list.filters.status !== "all" || q || list.filters.date_from || list.filters.date_to ? "Try a different status, search or date range." : "They appear here as soon as a merchant creates one."}
          </Empty>} />
        <Pagination page={list.data.page} pages={list.data.pages} total={list.data.total} size={list.size} onPage={list.setPage} onSize={list.setSize} />
      </Section>

      {open && <TxnDetail key={open} kind={kind} id={open} onClose={() => setOpen(null)} onChanged={() => { list.load(); loadCounts(); }} />}

      <style>{`
        .gwt-live { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #5b6170; }
        .gwt-pad { padding: 12px 18px 0; }
        .gwt-list { display: flex; flex-direction: column; }
        .gwt-item { display: flex; flex-direction: column; gap: 8px; padding: 12px 0; border-bottom: 1px solid #F0F2F6; min-width: 0; }
        .gwt-item:first-child { padding-top: 0; }
        .gwt-item:last-child { border-bottom: 0; padding-bottom: 0; }
        .gwt-item-head { display: flex; flex-direction: row; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .gwt-neg { color: #c5221f; } .gwt-pos { color: #0f7a45; }
      `}</style>
    </Page>
  );
}

function TxnDetail({ kind, id, onClose, onChanged }) {
  const payin = kind === "payins";
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resolve, setResolve] = useState(null);
  const [resolving, setResolving] = useState(false);
  const load = useCallback(() => god.gwDetail(kind, id).then(setT).catch((e) => toast.error(e.message)), [kind, id]);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 5000);

  const refresh = async () => {
    setBusy(true);
    try {
      setT(await god.gwRefresh(kind, id));
      onChanged();
      toast.success("Refreshed from ProtonPay");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doResolve = async () => {
    setResolving(true);
    try {
      setT(await god.gwResolve(id, resolve));
      setResolve(null);
      onChanged();
      toast.success("Resolved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResolving(false);
    }
  };

  if (!t) return <Panel title="Loading…" onClose={onClose}><Section><Skeleton rows={6} /></Section></Panel>;

  const upstreamId = payin ? t.payin_id : t.payout_id;
  const cbs = t.upstream_callbacks || [];
  const dels = t.deliveries || [];
  const ledger = t.ledger || [];
  const raw = Object.fromEntries(Object.entries(t).filter(([k]) => !["events", "upstream_callbacks", "deliveries", "ledger"].includes(k)));
  const hasError = t.error_code || t.error_message || t.upstream_error || t.assignment_error || t.failure_reason;

  return (
    <Panel width={620} onClose={onClose} title={payin ? "Payin" : "Payout"} sub={t.merchant_name}
      actions={<>
        {t.status === "review" && <Button variant="primary" size="sm" onClick={() => setResolve({ outcome: "success", note: "", utr: "" })}>Resolve</Button>}
        <Button size="sm" icon={RefreshCw} disabled={busy} onClick={refresh}>{busy ? "Checking…" : "Refresh status"}</Button>
      </>}>
      <PanelHero kicker={`${t.merchant_name || "—"} · ${payin ? "Payin" : "Payout"}`} title={inr(rup(t.amount_paisa))}
        meta={<><StatusPill status={t.status} /><span>{when(t.created_at, true)}</span>{t.source && <Pill>{t.source}</Pill>}</>}
        reference={t.order_ref} />

      {t.status === "review" && (
        <Callout tone="red" title="Held for review">The bank network didn't give a clear answer. Confirm the real outcome with ProtonPay, then resolve.</Callout>
      )}

      {payin ? (
        <Section title="Payment" icon={CreditCard}>
          <Rows items={[
            ["Amount", inr(rup(t.amount_paisa))],
            t.amount_received_paisa != null && ["Amount received", inr(rup(t.amount_received_paisa))],
            ["Credited to merchant", inr(rup(t.credited_net_paisa || 0))],
            ["UTR", t.payment_id, { mono: true, copy: true }],
            ["Payer VPA", t.payer_vpa, { mono: true, copy: true }],
            ["Mode", t.mode],
            ["Channel", t.channel],
            ["Source", t.source],
            t.payin_url && ["Payment page", t.payin_url, { mono: true, copy: true }],
          ]} />
        </Section>
      ) : (
        <Section title="Beneficiary" icon={User}>
          <Rows items={[
            ["Account holder", t.account_holder_name],
            ["Account number", t.account_number, { mono: true, copy: true }],
            ["IFSC", t.ifsc_code, { mono: true, copy: true }],
            ["Bank", t.bank_name],
            ["UTR", t.payment_id, { mono: true, copy: true }],
            ["Refunded", t.refunded ? <Pill tone="amber">Refunded to balance</Pill> : "No"],
            ["Source", t.source],
          ]} />
        </Section>
      )}

      {payin && (t.username || t.phone || t.email || t.user_id) && (
        <Section title="Customer" icon={User}>
          <Rows hideEmpty items={[
            ["Name", t.username],
            ["Phone", t.phone, { mono: true, copy: true }],
            ["Email", t.email, { copy: true }],
            ["Customer id", t.user_id, { mono: true, copy: true }],
          ]} />
        </Section>
      )}

      <Section title="References" icon={FileText}>
        <Rows items={[
          ["Order id", t.order_ref, { mono: true, copy: true }],
          ["ProtonPay order id", t.merchant_order_id, { mono: true, copy: true }],
          [payin ? "ProtonPay payin id" : "ProtonPay payout id", upstreamId, { mono: true, copy: true }],
          ["SuperCash id", t.id, { mono: true, copy: true }],
          t.callback_url && ["Callback URL", t.callback_url, { mono: true, copy: true }],
        ]} />
      </Section>

      <Section title="Dates" icon={Clock}>
        <Rows items={[
          ["Created", when(t.created_at, true)],
          ["Updated", t.updated_at ? `${when(t.updated_at, true)} · ${ago(t.updated_at)}` : null],
          ["Completed", t.completed_at ? when(t.completed_at, true) : null],
        ]} />
      </Section>

      {hasError && (
        <Section title="Errors" icon={FileText}>
          <Rows hideEmpty items={[
            ["Failure reason", t.failure_reason],
            ["Error code", t.error_code, { mono: true }],
            ["Error", t.error_message],
            ["Assignment error", t.assignment_error ? `${t.assignment_error}${t.assignment_error_code ? ` (${t.assignment_error_code})` : ""}` : null],
          ]} />
          {t.upstream_error && <div style={{ marginTop: 10 }}><Code value={t.upstream_error} /></div>}
        </Section>
      )}

      <Section title="Timeline" icon={Clock}>
        <Timeline events={t.events} />
      </Section>

      <Section title="Bank callbacks" sub={`${cbs.length} from ProtonPay`} icon={Radio}>
        {cbs.length ? (
          <div className="gwt-list">
            {cbs.map((c) => (
              <div key={c.id} className="gwt-item">
                <div className="gwt-item-head">
                  <span className="k-mono k-faint">{when(c.received_at, true)} · {c.kind} · {c.ip || "—"}</span>
                  <Pill tone={c.verified ? "green" : "amber"} dot>{c.claimed_status || "?"} → {c.verified_status || "unverified"}</Pill>
                </div>
                {c.note && <Callout tone="amber">{c.note}</Callout>}
                <Code value={c.payload} />
              </div>
            ))}
          </div>
        ) : <div className="k-faint">None received.</div>}
      </Section>

      <Section title="Merchant callbacks" sub={`${dels.length} sent`} icon={Webhook}>
        {dels.length ? (
          <div className="gwt-list">
            {dels.map((d) => (
              <div key={d.id} className="gwt-item">
                <div className="gwt-item-head">
                  <span className="k-mono">{d.event} → {d.url || "no URL"}</span>
                  <StatusPill status={d.status} />
                </div>
                {(d.attempts > 0 || d.last_error) && <span className="k-faint" style={{ fontSize: 12.5 }}>{d.attempts} attempt(s){d.last_error ? ` · ${d.last_error}` : ""}</span>}
              </div>
            ))}
          </div>
        ) : <div className="k-faint">None.</div>}
      </Section>

      <Section title="Ledger rows" icon={BookOpen}>
        {ledger.length ? (
          <div className="gwt-list">
            {ledger.map((l) => (
              <div key={l.id} className="gwt-item gwt-item-head">
                <Cell mono main={l.kind} sub={when(l.created_at, true)} />
                <b className={l.amount_paisa >= 0 ? "gwt-pos" : "gwt-neg"}>{l.amount_paisa >= 0 ? "+" : "−"}{inr(Math.abs(rup(l.amount_paisa)))}</b>
              </div>
            ))}
          </div>
        ) : <div className="k-faint">No balance movements.</div>}
      </Section>

      <Section title="Raw record" icon={FileText}>
        <Code value={raw} />
      </Section>

      {resolve && (
        <Dialog title="Resolve payout in review" onClose={() => setResolve(null)} footer={<>
          <Button onClick={() => setResolve(null)}>Cancel</Button>
          <Button variant={resolve.outcome === "failed" ? "danger" : "primary"} disabled={resolving || resolve.note.trim().length < 3} onClick={doResolve}>
            {resolving ? "Resolving…" : `Resolve as ${resolve.outcome}`}
          </Button>
        </>}>
          <Callout tone="amber" title="Confirm with ProtonPay first">
            Check the real outcome with ProtonPay before resolving. "Failed" refunds {inr(rup(t.amount_paisa + (t.fee_paisa || 0)))} to the merchant.
          </Callout>
          <Field label="Outcome">
            <Select value={resolve.outcome} onChange={(e) => setResolve((r) => ({ ...r, outcome: e.target.value }))}
              options={[["success", "Paid (success)"], ["failed", "Not paid (failed → refund)"]]} />
          </Field>
          {resolve.outcome === "success" && (
            <Field label="UTR">
              <Input mono value={resolve.utr} onChange={(e) => setResolve((r) => ({ ...r, utr: e.target.value }))} />
            </Field>
          )}
          <Field label="Note" hint="At least 3 characters. Saved in the payout's timeline.">
            <Input value={resolve.note} onChange={(e) => setResolve((r) => ({ ...r, note: e.target.value }))} placeholder="Confirmed with ProtonPay support ticket #…" />
          </Field>
        </Dialog>
      )}
    </Panel>
  );
}
